import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { GlassCard } from '@/components/GlassCard';
import { Loader2, Mail, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Seo } from '@/components/Seo';

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [delivery, setDelivery] = useState('email');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setDelivery(data?.delivery || 'email');
      setSent(true);
      toast.success(data?.message || 'Request received.');
    } catch (err) {
      // We still treat it as sent to avoid email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen th-hero-radial">
      <Seo
        title="Forgot Password | Thirrsty.com"
        description="Forgot your Thirrsty.com password? Enter your email and our team will send you a secure password reset link."
        path="/auth/forgot"
        noindex
      />
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <div className="mb-6"><Logo asDownloadTrigger /></div>

        <GlassCard className="p-6 th-float-in">
          <button
            type="button"
            onClick={() => nav('/auth/login')}
            className="mb-4 inline-flex items-center gap-1 text-xs text-white/65 hover:text-white"
            data-testid="forgot-back-button"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </button>

          {!sent ? (
            <>
              <h1 className="font-display text-3xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Forgot password?</h1>
              <p className="mt-1 text-sm text-white/70">
                Enter the email on your Thirrsty account and we'll send you a secure reset link.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email" className="text-white/80">Email</Label>
                  <Input
                    id="email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit" disabled={loading || !email.trim()}
                  className="h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold disabled:opacity-60"
                  data-testid="forgot-submit-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 th-spin" /> : (<><Mail className="mr-2 h-4 w-4" /> Send reset request</>)}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-2" data-testid="forgot-success-block">
              <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-emerald-500/15 text-emerald-300">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h2 className="font-display text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                {delivery === 'email' ? 'Check your email' : 'Request received'}
              </h2>
              <p className="mt-2 text-sm text-white/70">
                {delivery === 'email' ? (
                  <>If <span className="text-white font-medium">{email}</span> is registered with Thirrsty, we've sent a password reset link. Check your inbox and spam folder — the link expires in 30 minutes.</>
                ) : (
                  <>If <span className="text-white font-medium">{email}</span> is registered with Thirrsty, our team will reach out with a secure password reset link within 24 hours.</>
                )}
              </p>
              <p className="mt-3 text-xs text-white/50">
                Need it sooner? Call us at <a href="tel:+14105047466" className="text-[#F6B24A] hover:underline">(410) 504-7466</a>.
              </p>
              <Button
                onClick={() => nav('/auth/login')}
                className="mt-5 h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
                data-testid="forgot-back-to-login-button"
              >
                Back to sign in
              </Button>
            </div>
          )}
        </GlassCard>

        <p className="mt-auto pt-8 text-center text-xs text-white/40">
          Remembered it? <Link to="/auth/login" className="underline" data-testid="forgot-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
